import axios from 'axios';
import { sanitizeStoredUser } from '../utils/auth';

export const API_BASE_URL =
  process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export const API_ORIGIN = API_BASE_URL.replace(/\/api\/?$/, '');

const API = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

// Add Authorization Token Interceptor
API.interceptors.request.use((req) => {
  const token = localStorage.getItem('token');
  if (token) {
    req.headers.Authorization = `Bearer ${token}`;
  }
  return req;
});
API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      !originalRequest?._retry &&
      !originalRequest?.url?.includes('/users/login') &&
      !originalRequest?.url?.includes('/users/refresh-token')
    ) {
      originalRequest._retry = true;

      try {
        const { data } = await API.post('/users/refresh-token');
        const user = sanitizeStoredUser(data);
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(user));
        originalRequest.headers.Authorization = `Bearer ${data.token}`;
        return API(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem('refreshToken');
      }
    }

    if (error.response && error.response.status === 401) {
        // Token expired or invalid, log out the user
        localStorage.removeItem('token'); // Remove token from localStorage
        localStorage.removeItem('refreshToken');

        // Trigger logout action in the component
        window.dispatchEvent(new Event('logout')); // Custom event to trigger logout
  
        window.location.href = '/login'; // Redirect to the login page
    }

    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);
// Auth-related APIs
export const loginUser = (data) => API.post('/users/login', data);
export const logoutUser = () => API.post('/users/logout');
export const getCurrentUser = () => API.get('/users/me');
export const registerUser = (data) => API.post('/users/register', data);
export const changePassword = (data) => API.put('/users/change-password', data);
export const refreshAccessToken = () => API.post('/users/refresh-token');

// User management APIs
export const fetchUsers = (page, limit, search = '', role = '') =>
  API.get('/users/getusers', {
    params: {
      page,
      limit,
      search,
      ...(role ? { role } : {}),
    },
  });

export const searchUsers = (search, page = 1, limit = 20) =>
  API.get('/users/search', { params: { search, page, limit } });

export const updateUser = (id, data) => API.put(`/users/users/${id}`, data);
export const toggleUserStatus = (id, isActive) =>
  API.put(`/users/users/${id}/status`, { isActive });
export const deleteUser = (id) => API.delete(`/users/users/${id}`);


// Push Notification-related APIs
export const enablePushNotifications = () => API.post('/notifications/enable');
export const disablePushNotifications = () => API.post('/notifications/disable');

export const rolesUser = () => API.get('/roles/');
export const assignableRolesUser = () => API.get('/roles/assignable');
export const fetchRoles = (page, limit, search = '') =>
  API.get('/roles/manage', { params: { page, limit, search } });
export const departmentsUser = () => API.get('/departments/');
export const fetchDepartments = (page, limit, search = '') =>
  API.get('/departments/manage', { params: { page, limit, search } });

export const createRole = (data) => API.post('/roles/', data);
export const updateRole = (id, data) => API.put(`/roles/${id}`, data);
export const toggleRoleStatus = (id, isActive) =>
  API.put(`/roles/${id}/status`, { isActive });
export const deleteRole = (id) => API.delete(`/roles/${id}`);

export const createDepartment = (data) => API.post('/departments/', data);
export const updateDepartment = (id, data) => API.put(`/departments/${id}`, data);
export const toggleDepartmentStatus = (id, isActive) =>
  API.put(`/departments/${id}/status`, { isActive });
export const deleteDepartment = (id) => API.delete(`/departments/${id}`);

export const getAuditLogs = (params = {}) => API.get('/audit-logs', { params });

export const getEmailSettings = () => API.get('/settings/email');
export const updateEmailSettings = (data) => API.put('/settings/email', data);
export const sendTestEmailSettings = (data) => API.post('/settings/email/test', data);

export const getEmailTemplates = () => API.get('/settings/email-templates');
export const getEmailTemplate = (type) => API.get(`/settings/email-templates/${encodeURIComponent(type)}`);
export const updateEmailTemplate = (type, data) =>
  API.put(`/settings/email-templates/${encodeURIComponent(type)}`, data);
export const resetEmailTemplate = (type) =>
  API.post(`/settings/email-templates/${encodeURIComponent(type)}/reset`);

export const getBrandSettings = () =>
  axios.get(`${API_BASE_URL}/settings/brand`);
export const updateBrandSettings = (data) => API.put('/settings/brand', data);
export const resetBrandSettings = () => API.post('/settings/brand/reset');
export const uploadBrandAssets = (formData) =>
  API.post('/settings/brand/assets', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

export const getMyNotifications = (params = {}) => API.get('/notifications', { params });
export const markNotificationRead = (id) => API.put(`/notifications/${id}/read`);
export const markAllNotificationsRead = () => API.put('/notifications/read-all');


// Incident-related APIs
export const getRoleBasedIncidents = (page = 1, limit = 10, status = 'open', extraParams = {}) =>
  API.get('/incidents/dashboard', { params: { page, limit, status, ...extraParams } });

export const createIncident = (data) => API.post('/incidents/create', data);

// export const updateIncident = (id, data) => API.put(`/incidents/update/${id}`, data);
export const updateIncident = (id, data) => {

  const formData = new FormData();

  formData.append('data', JSON.stringify(data));

  // USER COMMENT FILES
  if (data?.UserComment?.attachments) {
    data.UserComment.attachments.forEach(f => {
      if (f.fileObj) formData.append('files', f.fileObj);
    });
  }

  // HOD FILES
  if (data?.HODDetails?.attachments) {
    data.HODDetails.attachments.forEach(f => {
      if (f.fileObj) formData.append('files', f.fileObj);
    });
  }

  return API.put(`/incidents/update/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const getIncident = (id) => API.get(`/incidents/${id}`);
export const getIncidentStatusLog = (id) => API.get(`/incidents/${id}/status-log`);
export const getLatestIncidentRefNo = () => API.get('/incidents/getLatestRefno');

export const downloadIncidentExport = (id, type, params = {}) =>
  API.get(`/incidents/${id}/export/${type}`, {
    params,
    responseType: 'blob',
  });

export const getIncidentReports = (params = {}) =>
  API.get('/incidents/reports', { params });

export const downloadReportExcel = (params = {}) =>
  API.get('/incidents/reports/export/excel', {
    params,
    responseType: 'blob',
  });
